import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { RestoreBackup } from "@/components/RestoreBackup";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const mb = (b) => (b < 1048576 ? `${(b / 1024).toLocaleString("it-IT", { maximumFractionDigits: 1 })} KB` : `${(b / 1048576).toLocaleString("it-IT", { maximumFractionDigits: 2 })} MB`);

const Bar = ({ pct }) => {
  if (pct == null) return <span className="text-xs text-slate-400">nessun limite</span>;
  const tone = pct >= 100 ? "bg-rose-600" : pct >= 80 ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-200"><div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      <span className="font-mono text-xs">{pct.toLocaleString("it-IT")}%</span>
    </div>
  );
};

export default function Storage() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const backup = async () => {
    setBusy(true);
    try { await download("/admin/backup.zip"); toast.success("Copia di sicurezza scaricata"); } catch (e) { toast.error(e.message || "Download non riuscito"); }
    setBusy(false);
  };
  useEffect(() => { api.get("/admin/storage").then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div>
      <PageHeader title="Spazio usato" subtitle="Dati di ogni azienda e file caricati nell'archivio. Il limite si imposta nei Pacchetti XML e vale per i file caricati.">
        <Button data-testid="backup-download-button" disabled={busy} onClick={backup} className="bg-blue-600 hover:bg-blue-700"><DatabaseBackup className="mr-2 h-4 w-4" />{busy ? "Preparazione…" : "Scarica copia di sicurezza"}</Button>
        <RestoreBackup />
      </PageHeader>
      <p className="mb-4 text-xs text-slate-500">La copia contiene fatture (con XML), anagrafiche, articoli, prima nota, corrispettivi, DDT, ordini e impostazioni di tutte le aziende in un unico file ZIP. Password e chiavi sono escluse; i file caricati restano nell'archivio.</p>
      {r && (
        <>
          <p className="mb-4 text-sm text-slate-600" data-testid="storage-total">Totale portale: <b className="font-mono">{mb(r.totale)}</b></p>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <Table data-testid="storage-table">
              <TableHeader><TableRow><TableHead>Azienda</TableHead><TableHead>Pacchetto</TableHead><TableHead className="text-right">Dati</TableHead><TableHead className="text-right">File</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Limite file</TableHead><TableHead>Uso file</TableHead></TableRow></TableHeader>
              <TableBody>
                {r.aziende.map((c) => (
                  <TableRow key={c.id} data-testid={`storage-row-${c.id}`}>
                    <TableCell className="font-medium">{c.denominazione}{c.demo && <span className="ml-2 text-xs text-slate-400">demo</span>}</TableCell>
                    <TableCell>{c.pacchetto || "-"}</TableCell>
                    <TableCell className="text-right font-mono">{mb(c.dati)}</TableCell>
                    <TableCell className="text-right font-mono">{mb(c.file)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{mb(c.dati + c.file)}</TableCell>
                    <TableCell>{c.limite_mb ? `${c.limite_mb.toLocaleString("it-IT")} MB` : "-"}</TableCell>
                    <TableCell><Bar pct={c.percentuale} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
